import React, { useMemo, useState } from 'react';
import { Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import '../changesCalendar.css';
import type { ChangeCalendarHoliday, ChangeCalendarItem, ChangeMeetingLite } from '../../../types/changes';
import { labelChangeStatus } from '../changeLabels';
import { formatChangePublicId } from '../changePublicId';
import {
  DEFAULT_CAMUD_WEEKDAY,
  DEFAULT_CHANGE_WINDOW_WEEKDAY,
  DEFAULT_RISK_COMMITTEE_WEEKDAY,
  formatGoLiveWindow,
  formatGovernanceDate,
  formatGovernanceWindow,
  isoDateKey,
} from '../goLiveWindow';
import { addDays, formatDateInput, type CalendarViewMode } from '../hooks/useCalendarWindow';

const WEEK_LABELS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
const MAX_WINDOW_DAYS = 31;
/** Mudanças visíveis por dia no modo executivo antes de “mostrar mais”. */
const EXEC_CHANGE_CAP = 8;

function isValidDay(value: Date): boolean {
  return value instanceof Date && !Number.isNaN(value.getTime());
}

function weekdayShort(day: Date): string {
  if (!isValidDay(day)) return '';
  const label = day.toLocaleDateString('pt-BR', { weekday: 'short' });
  return label.includes('Invalid') ? '' : label;
}

function dayNumberLabel(day: Date): string {
  if (!isValidDay(day)) return '';
  const number = day.getDate();
  return Number.isFinite(number) ? String(number) : '';
}

function formatMeetingWhen(value?: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function meetingPautaLabel(count: number): string {
  if (count === 1) return '1 mudança';
  return `${count} mudanças`;
}

function formatMeetingClock(total: number): string {
  const safe = Math.max(0, Math.floor(total));
  const hours = Math.floor(safe / 3600);
  const minutes = String(Math.floor((safe % 3600) / 60)).padStart(2, '0');
  const seconds = String(safe % 60).padStart(2, '0');
  if (hours > 0) return `${hours}:${minutes}:${seconds}`;
  return `${minutes}:${seconds}`;
}

function EmptyDayCell({ minHeight }: { minHeight: number }) {
  return (
    <div className="border rounded bg-light" style={{ minHeight, opacity: 0.35 }} aria-hidden="true" />
  );
}

function leadingMonthDays(first: Date | undefined, count: number): Date[] {
  if (!first || !isValidDay(first) || count <= 0) return [];
  return Array.from({ length: count }, (_, index) => addDays(first, index - count));
}

function windowDayKeys(start?: string | null, end?: string | null): string[] {
  const startKey = isoDateKey(start || '');
  if (!startKey) return [];
  const endKey = isoDateKey(end || start || '') || startKey;
  if (endKey <= startKey) return [startKey];

  const keys: string[] = [];
  const cursor = new Date(`${startKey}T00:00:00`);
  const limit = new Date(`${endKey}T00:00:00`);
  while (cursor <= limit && keys.length < MAX_WINDOW_DAYS) {
    keys.push(formatDateInput(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return keys;
}

interface CalendarCard extends ChangeCalendarItem {
  marks: string[];
}

export type CalendarDisplayMode = 'executive' | 'detailed';

interface Props {
  days: Date[];
  leadingBlanks: number;
  items: ChangeCalendarItem[];
  meetings?: ChangeMeetingLite[];
  holidays?: ChangeCalendarHoliday[];
  viewMode: CalendarViewMode;
  windowWeekday: number;
  /** Executiva: chips compactos; detalhada: cards completos (legado). */
  displayMode?: CalendarDisplayMode;
  highlightedId?: number | null;
  highlightedMeetingId?: number | null;
  onCardClick?: (item: ChangeCalendarItem) => void;
  onMeetingClick?: (meeting: ChangeMeetingLite) => void;
}

function isTodayKey(key: string): boolean {
  return key === formatDateInput(new Date());
}

function markDotClass(mark: string): string {
  if (mark.startsWith('CAMUD')) return 'camud';
  if (mark === 'Riscos') return 'riscos';
  return 'go-live';
}

const ChangesCalendarGrid: React.FC<Props> = ({
  days,
  leadingBlanks,
  items,
  meetings = [],
  holidays = [],
  viewMode,
  windowWeekday,
  displayMode = 'detailed',
  highlightedId = null,
  highlightedMeetingId = null,
  onCardClick,
  onMeetingClick,
}) => {
  const executive = displayMode === 'executive';
  const [expandedDayKeys, setExpandedDayKeys] = useState<Set<string>>(() => new Set());

  const toggleDayExpanded = (dayKey: string) => {
    setExpandedDayKeys((prev) => {
      const next = new Set(prev);
      if (next.has(dayKey)) next.delete(dayKey);
      else next.add(dayKey);
      return next;
    });
  };
  const itemsByDay = useMemo(() => {
    const grouped: Record<string, CalendarCard[]> = {};
    for (const item of items) {
      const goLiveDays = windowDayKeys(item.go_live_start_at, item.go_live_end_at);
      const riskDays = windowDayKeys(item.risk_committee_start_at, item.risk_committee_end_at);
      const camudDay = isoDateKey(item.camud_go_no_go_date || '');
      const allDays = Array.from(new Set([...goLiveDays, ...riskDays, ...(camudDay ? [camudDay] : [])]));
      for (const key of allDays) {
        const marks: string[] = [];
        if (goLiveDays.includes(key)) marks.push('Go Live');
        if (riskDays.includes(key)) marks.push('Riscos');
        if (camudDay === key) marks.push('CAMUD');
        grouped[key] = grouped[key] || [];
        grouped[key].push({ ...item, marks });
      }
    }
    return grouped;
  }, [items]);

  const meetingsByDay = useMemo(() => {
    const grouped: Record<string, ChangeMeetingLite[]> = {};
    for (const meeting of meetings) {
      const key = isoDateKey(meeting.scheduled_at);
      if (!key) continue;
      grouped[key] = grouped[key] || [];
      grouped[key].push(meeting);
    }
    return grouped;
  }, [meetings]);

  const holidayByDay = useMemo(() => {
    const mapped: Record<string, string> = {};
    for (const holiday of holidays) {
      const key = isoDateKey(holiday.date);
      if (key) mapped[key] = holiday.name;
    }
    return mapped;
  }, [holidays]);

  const cellMinHeight = executive
    ? viewMode === 'week'
      ? 160
      : 140
    : viewMode === 'week'
      ? 200
      : 140;
  const leadingDays = leadingMonthDays(days.find(isValidDay), leadingBlanks);

  return (
    <div className="overflow-auto">
      <div
        className={`changes-calendar-grid${executive ? ' is-executive' : ''}`}
        style={{
          display: 'grid',
          gridTemplateColumns: executive
            ? 'repeat(7, minmax(168px, 1fr))'
            : 'repeat(7, minmax(130px, 1fr))',
          gap: 8,
          minWidth: viewMode === 'month' ? (executive ? 1180 : 910) : executive ? 1180 : undefined,
        }}
      >
        {WEEK_LABELS.map((label) => (
          <div key={label} className="changes-cal-weekhead text-center">
            {label}
          </div>
        ))}

        {Array.from({ length: Math.max(0, leadingBlanks) }, (_, index) => {
          const day = leadingDays[index];
          const minHeight = viewMode === 'week' ? 160 : 110;
          if (!day || !dayNumberLabel(day)) {
            return <EmptyDayCell key={`lead-empty-${index}`} minHeight={minHeight} />;
          }
          return (
            <div
              key={`lead-${formatDateInput(day)}`}
              className="border rounded bg-light"
              style={{ minHeight, opacity: 0.45 }}
            >
              <div className="d-flex justify-content-between align-items-center p-2 mb-0">
                <strong className="small">{dayNumberLabel(day)}</strong>
                <span className="small text-muted d-none d-md-inline">{weekdayShort(day)}</span>
              </div>
            </div>
          );
        })}

        {days.map((day, index) => {
          if (!isValidDay(day) || !dayNumberLabel(day)) {
            return <EmptyDayCell key={`invalid-${index}`} minHeight={cellMinHeight} />;
          }
          const key = formatDateInput(day);
          const dayItems = itemsByDay[key] || [];
          const dayMeetings = meetingsByDay[key] || [];
          const holidayName = holidayByDay[key];
          const pythonWeekday = (day.getDay() + 6) % 7;
          const isGoLiveDay = pythonWeekday === (windowWeekday ?? DEFAULT_CHANGE_WINDOW_WEEKDAY);
          const isRiskDay = pythonWeekday === DEFAULT_RISK_COMMITTEE_WEEKDAY;
          const isCamudDay = pythonWeekday === DEFAULT_CAMUD_WEEKDAY;
          const dayClasses = [
            'changes-calendar-day',
            'p-2',
            isTodayKey(key) ? 'is-today' : '',
            holidayName ? 'is-holiday' : '',
            !holidayName && isGoLiveDay ? 'is-go-live' : '',
            !holidayName && isRiskDay ? 'is-risk' : '',
            !executive && (isGoLiveDay || isCamudDay) ? 'border-primary' : '',
            !executive && isRiskDay ? 'border-success' : '',
            !executive && holidayName ? 'border-warning' : '',
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <div
              key={key}
              className={dayClasses}
              style={{ minHeight: cellMinHeight }}
            >
              <div className="changes-calendar-day-head">
                <span className="changes-calendar-day-num">{dayNumberLabel(day)}</span>
                <span className="changes-calendar-day-week">{weekdayShort(day)}</span>
              </div>
              {holidayName ? (
                <div className="mb-1">
                  {executive ? (
                    <span className="small text-warning-emphasis" title={holidayName}>
                      Feriado
                    </span>
                  ) : (
                    <Badge bg="warning" text="dark" className="fw-normal text-wrap text-start">
                      Feriado: {holidayName}
                    </Badge>
                  )}
                </div>
              ) : null}
              {executive ? (
                (() => {
                  const sortedChanges = [...dayItems].sort((a, b) => a.id - b.id);
                  const dayExpanded = expandedDayKeys.has(key);
                  const visibleChanges = dayExpanded
                    ? sortedChanges
                    : sortedChanges.slice(0, EXEC_CHANGE_CAP);
                  const hiddenChanges = sortedChanges.length - visibleChanges.length;
                  const totalCount = dayMeetings.length + sortedChanges.length;
                  return (
                    <>
                      {totalCount > 0 ? (
                        <div className="changes-cal-day-stats mb-1">
                          {dayMeetings.length
                            ? `${dayMeetings.length} reuni${dayMeetings.length === 1 ? 'ão' : 'ões'}`
                            : null}
                          {dayMeetings.length && sortedChanges.length ? ' · ' : null}
                          {sortedChanges.length
                            ? `${sortedChanges.length} mudanç${sortedChanges.length === 1 ? 'a' : 'as'}`
                            : null}
                        </div>
                      ) : null}
                      <div className="changes-cal-exec-list">
                        {dayMeetings.map((meeting) => {
                          const isHighlighted = highlightedMeetingId === meeting.id;
                          const isDimmed =
                            (highlightedMeetingId != null && !isHighlighted) || highlightedId != null;
                          const rowClass = [
                            'changes-cal-exec-row',
                            'is-meeting',
                            isHighlighted ? 'is-highlighted' : '',
                            isDimmed ? 'is-dimmed' : '',
                          ]
                            .filter(Boolean)
                            .join(' ');
                          const label = `${meeting.title} · ${meetingPautaLabel(meeting.item_count || 0)}`;
                          return onMeetingClick ? (
                            <button
                              key={`meeting-${meeting.id}`}
                              type="button"
                              className={rowClass}
                              aria-pressed={isHighlighted}
                              title={`Reunião CAB — ${meeting.title} (${meeting.status_label || meeting.status})`}
                              onClick={() => onMeetingClick(meeting)}
                            >
                              <span className="changes-cal-exec-id">CAB</span>
                              <span className="changes-cal-exec-title">{label}</span>
                            </button>
                          ) : (
                            <Link
                              key={`meeting-${meeting.id}`}
                              to={`/changes/cab/${meeting.id}`}
                              className={`${rowClass} text-decoration-none`}
                              title={meeting.title}
                            >
                              <span className="changes-cal-exec-id">CAB</span>
                              <span className="changes-cal-exec-title">{label}</span>
                            </Link>
                          );
                        })}
                        {visibleChanges.map((item) => {
                          const isHighlighted = highlightedId === item.id;
                          const isDimmed =
                            (highlightedId != null && !isHighlighted) || highlightedMeetingId != null;
                          const rowClass = [
                            'changes-cal-exec-row',
                            isHighlighted ? 'is-highlighted' : '',
                            isDimmed ? 'is-dimmed' : '',
                          ]
                            .filter(Boolean)
                            .join(' ');
                          const statusLabel = labelChangeStatus(item.status);
                          return onCardClick ? (
                            <button
                              key={`${item.id}-${item.marks.join('-')}`}
                              type="button"
                              className={rowClass}
                              aria-pressed={isHighlighted}
                              title={`${formatChangePublicId(item.id)} · ${item.title} (${statusLabel})`}
                              onClick={() => onCardClick(item)}
                            >
                              <span className="changes-cal-exec-id">{formatChangePublicId(item.id)}</span>
                              <span className="changes-cal-exec-title">{item.title}</span>
                              {item.marks.length ? (
                                <span className="changes-cal-exec-marks" aria-hidden="true">
                                  {item.marks.map((mark) => (
                                    <span
                                      key={mark}
                                      className={`changes-cal-mark-dot ${markDotClass(mark)}`}
                                    />
                                  ))}
                                </span>
                              ) : null}
                            </button>
                          ) : (
                            <Link
                              key={`${item.id}-${item.marks.join('-')}`}
                              to={`/changes/${item.id}`}
                              className={`${rowClass} text-decoration-none`}
                              title={item.title}
                            >
                              <span className="changes-cal-exec-id">{formatChangePublicId(item.id)}</span>
                              <span className="changes-cal-exec-title">{item.title}</span>
                            </Link>
                          );
                        })}
                        {hiddenChanges > 0 && !dayExpanded ? (
                          <button
                            type="button"
                            className="changes-cal-exec-more"
                            onClick={() => toggleDayExpanded(key)}
                          >
                            +{hiddenChanges} mudança{hiddenChanges === 1 ? '' : 's'} neste dia
                          </button>
                        ) : null}
                        {dayExpanded && sortedChanges.length > EXEC_CHANGE_CAP ? (
                          <button
                            type="button"
                            className="changes-cal-exec-more"
                            onClick={() => toggleDayExpanded(key)}
                          >
                            Mostrar menos
                          </button>
                        ) : null}
                        {totalCount === 0 ? (
                          <span className="text-muted px-1 small">
                            —
                          </span>
                        ) : null}
                      </div>
                    </>
                  );
                })()
              ) : (
              <div className="d-flex flex-column gap-1">
                {dayMeetings.map((meeting) => {
                  const isHighlighted = highlightedMeetingId === meeting.id;
                  const isDimmed =
                    (highlightedMeetingId != null && !isHighlighted) || highlightedId != null;
                  const meetingBody = executive ? (
                    <>
                      <div className="d-flex justify-content-between align-items-center gap-1">
                        <span className="changes-cal-executive-title">{meeting.title}</span>
                        <Badge bg="secondary" className="fw-normal">
                          {meeting.status_label || meeting.status}
                        </Badge>
                      </div>
                      <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                        CAB · {meeting.kind_label} · {meetingPautaLabel(meeting.item_count || 0)}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="d-flex justify-content-between gap-1">
                        <span className="fw-semibold text-truncate">Reunião</span>
                        <Badge bg="secondary">{meeting.status_label || meeting.status}</Badge>
                      </div>
                      <div className="text-truncate">{meeting.title}</div>
                      <div className="text-muted" style={{ fontSize: 11 }}>
                        Tipo: {meeting.kind_label}
                      </div>
                      <div className="text-muted" style={{ fontSize: 11 }}>
                        Horário: {formatMeetingWhen(meeting.scheduled_at)}
                      </div>
                      <div className="text-muted" style={{ fontSize: 11 }}>
                        Pauta: {meetingPautaLabel(meeting.item_count || 0)}
                      </div>
                      <div className="text-muted" style={{ fontSize: 11 }}>
                        Tempo: {formatMeetingClock(meeting.total_elapsed_seconds || 0)}
                      </div>
                      <div className="d-flex flex-wrap gap-1 mt-1">
                        <Badge bg="dark" className="fw-normal">
                          Reunião
                        </Badge>
                        <Badge bg="info" className="fw-normal">
                          {meeting.kind_label}
                        </Badge>
                      </div>
                    </>
                  );
                  return (
                  <div
                    key={`meeting-${meeting.id}`}
                    className={`changes-cal-chip changes-cal-chip-meeting small ${
                      isHighlighted ? 'is-highlighted' : ''
                    } ${isDimmed ? 'is-dimmed' : ''}`}
                    data-highlighted={isHighlighted ? 'true' : 'false'}
                    style={{ cursor: onMeetingClick ? 'pointer' : undefined }}
                    title={`${meeting.title} (${meeting.status_label || meeting.status})`}
                  >
                    {onMeetingClick ? (
                      <button
                        type="button"
                        className="border-0 bg-transparent p-0 text-start text-body w-100"
                        aria-pressed={isHighlighted}
                        onClick={() => onMeetingClick(meeting)}
                      >
                        {meetingBody}
                      </button>
                    ) : (
                      meetingBody
                    )}
                    <Link
                      to={`/changes/cab/${meeting.id}`}
                      className={
                        executive
                          ? 'small link-secondary d-inline-block mt-1'
                          : 'btn btn-sm btn-outline-secondary mt-1'
                      }
                      onClick={(event) => event.stopPropagation()}
                    >
                      {meeting.status === 'encerrada' ? 'Ver tempos' : 'Abrir reunião'}
                    </Link>
                  </div>
                  );
                })}
                {dayItems.map((item) => {
                  const isHighlighted = highlightedId === item.id;
                  const isDimmed =
                    (highlightedId != null && !isHighlighted) || highlightedMeetingId != null;
                  const cardBody = executive ? (
                    <>
                      <div className="d-flex justify-content-between align-items-start gap-1">
                        <span className="changes-cal-executive-title">
                          {formatChangePublicId(item.id)} · {item.title}
                        </span>
                        <Badge bg="secondary" className="fw-normal flex-shrink-0">
                          {labelChangeStatus(item.status)}
                        </Badge>
                      </div>
                      {item.marks.length ? (
                        <div className="mt-1 text-muted" style={{ fontSize: 11 }}>
                          {item.marks.map((mark) => (
                            <span key={mark} className="me-2">
                              <span className={`changes-cal-mark-dot ${markDotClass(mark)}`} />
                              {mark}
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <div className="d-flex justify-content-between gap-1">
                        <span className="fw-semibold text-truncate">{formatChangePublicId(item.id)}</span>
                        <Badge bg="secondary">{labelChangeStatus(item.status)}</Badge>
                      </div>
                      <div className="text-truncate">{item.title}</div>
                      <div className="text-muted" style={{ fontSize: 11 }}>
                        Go Live: {formatGoLiveWindow(item.go_live_start_at, item.go_live_end_at)}
                      </div>
                      <div className="text-muted" style={{ fontSize: 11 }}>
                        Riscos:{' '}
                        {formatGovernanceWindow(item.risk_committee_start_at, item.risk_committee_end_at)}
                      </div>
                      <div className="text-muted" style={{ fontSize: 11 }}>
                        CAMUD: {formatGovernanceDate(item.camud_go_no_go_date)}
                      </div>
                      <div className="d-flex flex-wrap gap-1 mt-1">
                        {item.marks.map((mark) => (
                          <Badge
                            key={mark}
                            bg={mark.startsWith('CAMUD') ? 'info' : mark === 'Riscos' ? 'success' : 'primary'}
                            className="fw-normal"
                          >
                            {mark}
                          </Badge>
                        ))}
                      </div>
                    </>
                  );
                  const chipClass = `changes-cal-chip changes-cal-chip-change small ${
                    isHighlighted ? 'is-highlighted' : ''
                  } ${isDimmed ? 'is-dimmed' : ''}`;
                  const cardStyle: React.CSSProperties = executive
                    ? { cursor: onCardClick ? 'pointer' : undefined }
                    : {
                        background: isHighlighted ? '#fff3cd' : '#fff',
                        cursor: onCardClick ? 'pointer' : undefined,
                        opacity: isDimmed ? 0.4 : 1,
                        boxShadow: isHighlighted ? '0 0 0 2px #fd7e14' : '0 0 0 0 transparent',
                        transition:
                          'background-color 140ms ease, box-shadow 140ms ease, opacity 140ms ease',
                      };
                  if (onCardClick) {
                    return (
                      <div
                        key={`${item.id}-${item.marks.join('-')}`}
                        className={executive ? chipClass : 'border rounded px-1 py-1 small'}
                        style={cardStyle}
                        data-highlighted={isHighlighted ? 'true' : 'false'}
                        title={`${item.title} (${labelChangeStatus(item.status)})`}
                      >
                        <button
                          type="button"
                          className="border-0 bg-transparent p-0 text-start text-body w-100"
                          aria-pressed={isHighlighted}
                          onClick={() => onCardClick(item)}
                        >
                          {cardBody}
                        </button>
                        <Link
                          to={`/changes/${item.id}`}
                          className={
                            executive
                              ? 'small link-secondary d-inline-block mt-1'
                              : 'btn btn-sm btn-outline-secondary mt-1'
                          }
                          onClick={(event) => event.stopPropagation()}
                        >
                          Abrir mudança
                        </Link>
                      </div>
                    );
                  }
                  return (
                    <div
                      key={`${item.id}-${item.marks.join('-')}`}
                      className={executive ? chipClass : 'border rounded px-1 py-1 small'}
                      style={cardStyle}
                      data-highlighted={isHighlighted ? 'true' : 'false'}
                      title={`${item.title} (${labelChangeStatus(item.status)})`}
                    >
                      {cardBody}
                      <Link
                        to={`/changes/${item.id}`}
                        className={
                          executive
                            ? 'small link-secondary d-inline-block mt-1'
                            : 'btn btn-sm btn-outline-secondary mt-1'
                        }
                      >
                        Abrir mudança
                      </Link>
                    </div>
                  );
                })}
                {dayItems.length === 0 && dayMeetings.length === 0 ? (
                  <span className="text-muted" style={{ fontSize: 11 }}>
                    —
                  </span>
                ) : null}
              </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ChangesCalendarGrid;
